import React from 'react';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip/Tooltip';
import { FormikProps } from 'formik';
import i18n from 'i18n/config';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';

import { TradeDto } from '@alpha/fx-dtos';
import { APDatePicker } from '@alpha/ui-lib/ui/DatePicker';
import {
  Box,
  Input,
  MenuItem,
  Select,
  Typography,
} from '@alpha/ui-lib/ui/external';

import { MassDrawdownRequestForm } from '../formData';

import useStyles from './index.styles';

interface MassDrawdownDetailsProps {
  massDrawdownValueDate: string;
  handleSplitChange: (
    e: React.ChangeEvent<{ value: string | unknown }>
  ) => void;
  handleCurrencyChange: (
    e: React.ChangeEvent<{ value: string | unknown }>
  ) => void;
  handleAmountChange: (amount: number, trade: TradeDto) => void;
  requestForm: FormikProps<MassDrawdownRequestForm>;
  selectedTrades: TradeDto[];
}

const dropdownItems = (selectedTrades: TradeDto[]): React.ReactNode[] => (
  selectedTrades.length
    ? [
      <MenuItem value={selectedTrades[0].buyCurrencyCode}>
        {selectedTrades[0].buyCurrencyCode}
      </MenuItem>,
      <MenuItem value={selectedTrades[0].soldCurrencyCode}>
        {selectedTrades[0].soldCurrencyCode}
      </MenuItem>,
    ]
    : []
);

const MassDrawdownDetails: React.FC<MassDrawdownDetailsProps> = ({
  massDrawdownValueDate,
  handleSplitChange,
  handleCurrencyChange,
  handleAmountChange,
  requestForm,
  selectedTrades,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.detailsContainer}>
      {t('drawdown_details')}
      <Box display="flex" gridColumnGap={16} marginY="8px">
        <Tooltip title={t('to_modify_the_value_date')} placement="bottom-start" classes={{ tooltip: classes.tooltip }} arrow={false}>
          <div>
            <APDatePicker
              language={i18n.language}
              className="datePicker"
              value={massDrawdownValueDate}
              autoOk
              placeholder={t('select_value_date')}
              disablePast
              disabled
              onChange={() => {}}
              fullWidth
              style={{ width: '393px', borderRadius: '4px' }}
            />
          </div>
        </Tooltip>
        <Select
          value={requestForm.values?.splitType}
          id="split-select"
          data-testid="split-select"
          onChange={handleSplitChange}
          className={classes.dropdown}
          fullWidth
          disableUnderline
          style={{ width: '393px', borderRadius: '4px' }}
        >
          {[
            <MenuItem value="placeholder" disabled>
              <Typography variant="subtitle1" className={classes.placeholder}>
                {t('select_split_method')}
              </Typography>
            </MenuItem>,
            <MenuItem value="split-evenly">
              {t('split_amount_evenly')}
            </MenuItem>,
            <MenuItem value="manually-enter">
              {t('manually_enter_amounts')}
            </MenuItem>,
          ]}
        </Select>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="top"
        gridColumnGap={16}
      >
        <Select
          onChange={handleCurrencyChange}
          value={requestForm?.values?.fixCurrencyCode}
          id="currency-select"
          data-testid="currency-select"
          fullWidth
          disableUnderline
          className={classes.dropdown}
          placeholder={t('select_currency')}
        >
          {[
            <MenuItem value="placeholder" disabled>
              <Typography variant="subtitle1" className={classes.placeholder}>
                {t('select_currency')}
              </Typography>
            </MenuItem>,
            ...dropdownItems(selectedTrades),
          ]}
        </Select>
        <Tooltip
          title={(requestForm.values?.splitType === 'placeholder' || requestForm.values?.fixCurrencyCode === 'placeholder')
            ? t('please_select_a_split_method') : ''}
          placement="bottom-start"
          classes={{ tooltip: classes.tooltip }}
          arrow={false}
        >
          <Box >
            <NumberFormat
              fullWidth
              thousandSeparator
              disableUnderline
              allowLeadingZeros={false}
              id="even-split-amount"
              type="text"
              placeholder={
                requestForm.values?.splitType !== 'manually-enter'
                  ? t('enter_amount')
                  : t('enter_amount_below')
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => debounce(() => {
                const amount = parseFloat(e.target.value.replace(/,/g, ''));
                handleAmountChange(amount, selectedTrades[0].id);
              }, 100)()}
              // onBlur={onBlur}
              value={String(requestForm.values?.evenSplitAmount) || ''}
              // error={error}
              // helperText={helperText}
              inputProps={{
                style: { marginLeft: '16px' },
              }}
              disabled={requestForm.values?.splitType !== 'split-evenly' || requestForm.values?.fixCurrencyCode === 'placeholder'}
              className={clsx(
                (requestForm.values?.splitType === 'split-evenly' && requestForm.values?.fixCurrencyCode !== 'placeholder')
                  ? classes.evenSplitAmount
                  : classes.disabledEvenSplitAmount,
              )}
              customInput={Input}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default MassDrawdownDetails;
